import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, MapPin, IndianRupee, Clock, Users, Briefcase, X, RotateCcw, ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { PageHero, SectionHead, Reveal, OutlineButton } from "@/components/bits/Reveal";
import ApplyModal from "@/components/forms/ApplyModal";
import ProfileForm from "@/components/forms/ProfileForm";
import api from "@/lib/api";
import { IMAGES } from "@/data/content";
import { useSEO } from "@/lib/seo";
import { motion, AnimatePresence } from "framer-motion";

const SALARY_OPTIONS = [
  { label: "Any salary", value: "" },
  { label: "₹15,000+", value: "15000" },
  { label: "₹18,000+", value: "18000" },
  { label: "₹20,000+", value: "20000" },
  { label: "₹25,000+", value: "25000" },
];

const selectCls = "rounded-sm border border-ink/15 bg-white focus:ring-1 focus:ring-gold focus:border-gold h-11 text-base sm:text-sm w-full px-3 outline-none transition-colors duration-200";

function JobDetail({ job, onClose, onApply }) {
  return (
    <motion.div className="fixed inset-0 z-[70] bg-darknavy/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="job-detail-overlay">
      <motion.div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-t-md sm:rounded-sm" initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} data-testid="job-detail-modal">
        <div className="bg-darknavy px-6 sm:px-8 py-6 sticky top-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-lightgold font-bold mb-1.5">{job.company}</p>
              <h3 className="font-display text-2xl text-white">{job.title}</h3>
              <p className="text-white/55 text-sm mt-1 flex items-center gap-1.5"><MapPin size={13} /> {job.location}</p>
            </div>
            <button onClick={onClose} data-testid="job-detail-close" className="text-white/60 hover:text-lightgold p-1 transition-colors" aria-label="Close"><X size={20} /></button>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-7">
            {[["Qualification", job.qualification], ["Experience", job.experience], ["Salary / CTC", job.salary_display], ["Shift", job.shift], ["Type", job.employment_type], ["Openings", `${job.openings} positions`], ["Industry", job.industry], ["Gender", job.gender || "Any"], ["Apply Before", job.deadline || "Open"]].map(([k, v]) => (
              <div key={k} className="bg-steel rounded-sm p-3.5">
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-gold">{k}</p>
                <p className="text-sm font-semibold text-ink mt-1">{v}</p>
              </div>
            ))}
          </div>
          <h4 className="font-display text-lg text-ink mb-2">About this role</h4>
          <p className="text-sm text-muted leading-relaxed mb-6">{job.description}</p>
          {job.benefits?.length > 0 && (
            <>
              <h4 className="font-display text-lg text-ink mb-3">Benefits</h4>
              <div className="flex flex-wrap gap-2 mb-8">
                {job.benefits.map((b) => <span key={b} className="text-xs font-semibold text-navy bg-navy/5 border border-navy/15 px-3 py-1.5 rounded-sm">{b}</span>)}
              </div>
            </>
          )}
          <button onClick={() => onApply(job)} data-testid="job-detail-apply" className="btn-sweep w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-darknavy font-semibold text-sm px-8 py-4 rounded-sm hover:bg-lightgold transition-colors duration-300">
            Apply Now
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Careers() {
  useSEO(
    "Careers & Job Openings | Maqline Solutions Pvt Ltd — 500+ Industrial Jobs",
    "Search 500+ job openings with Maqline Solutions Pvt Ltd across manufacturing, automotive, electronics and engineering. ITI jobs, Diploma jobs, fresher and experienced roles in Bengaluru, Hosur and Chennai."
  );

  const [meta, setMeta] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [totalOpenings, setTotalOpenings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: "", location: "", industry: "", qualification: "", shift: "", employment_type: "", salary_min: "", gender: "", sort: "newest" });
  const [selected, setSelected] = useState({});
  const [detailJob, setDetailJob] = useState(null);
  const [applyJobs, setApplyJobs] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get("/jobs/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 12 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/jobs", { params });
      setJobs(data.jobs);
      setTotal(data.total);
      setPages(data.pages);
      setTotalOpenings(data.total_openings);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const setF = (k) => (e) => { setPage(1); setFilters((f) => ({ ...f, [k]: e.target.value })); };
  const reset = () => { setFilters({ q: "", location: "", industry: "", qualification: "", shift: "", employment_type: "", salary_min: "", gender: "", sort: "newest" }); setPage(1); };

  const selectedJobs = useMemo(() => jobs.filter((j) => selected[j.id]), [jobs, selected]);
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  return (
    <main data-testid="careers-page">
      <PageHero
        overline="Careers at Maqline"
        titleLines={[`${meta ? `${meta.total_openings}+` : "500+"} Job Opportunities`, "Available Right Now"]}
        description="Explore current job opportunities across manufacturing, automotive, engineering, electronics and other industrial sectors."
        image={IMAGES.engineerLaptop}
      >
        <motion.div className="mt-8 flex flex-wrap gap-3" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}>
          <a href="#jobs" data-testid="careers-view-jobs" className="btn-sweep inline-flex items-center gap-2 bg-gold text-darknavy font-semibold text-sm px-7 py-3.5 rounded-sm hover:bg-lightgold transition-colors duration-300">View All Jobs</a>
          <a href="#profile" data-testid="careers-submit-profile" className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold text-sm px-7 py-3.5 rounded-sm hover:border-gold hover:text-lightgold transition-colors duration-300">Submit Your Profile</a>
        </motion.div>
      </PageHero>

      <section id="jobs" className="bg-steel py-16 sm:py-24 scroll-mt-20" data-testid="jobs-board">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="bg-white border border-ink/10 rounded-sm p-5 sm:p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  data-testid="job-search-input"
                  className="rounded-sm border border-ink/15 bg-white focus:ring-1 focus:ring-gold focus:border-gold h-12 text-base sm:text-sm w-full pl-11 pr-4 outline-none transition-colors duration-200"
                  placeholder="Search by job title, company or keyword..."
                  value={filters.q}
                  onChange={setF("q")}
                />
              </div>
              <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters" className="lg:hidden inline-flex items-center justify-center gap-2 border border-ink/15 rounded-sm h-12 px-5 text-sm font-semibold text-ink">
                <SlidersHorizontal size={15} /> Filters
              </button>
              <select data-testid="sort-select" className={selectCls + " lg:w-52"} value={filters.sort} onChange={setF("sort")}>
                <option value="newest">Newest first</option>
                <option value="salary_desc">Salary: high to low</option>
                <option value="salary_asc">Salary: low to high</option>
                <option value="openings">Most openings</option>
              </select>
            </div>

            <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-3 ${showFilters ? "" : "hidden lg:grid"}`} data-testid="filters-grid">
              <select data-testid="filter-location" className={selectCls} value={filters.location} onChange={setF("location")}>
                <option value="">All locations</option>
                {meta?.locations.map((l) => <option key={l}>{l}</option>)}
              </select>
              <select data-testid="filter-industry" className={selectCls} value={filters.industry} onChange={setF("industry")}>
                <option value="">All industries</option>
                {meta?.industries.map((i) => <option key={i}>{i}</option>)}
              </select>
              <select data-testid="filter-qualification" className={selectCls} value={filters.qualification} onChange={setF("qualification")}>
                <option value="">All qualifications</option>
                {meta?.qualifications.map((q) => <option key={q}>{q}</option>)}
              </select>
              <select data-testid="filter-shift" className={selectCls} value={filters.shift} onChange={setF("shift")}>
                <option value="">All shifts</option>
                {meta?.shifts.map((s) => <option key={s}>{s}</option>)}
              </select>
              <select data-testid="filter-type" className={selectCls} value={filters.employment_type} onChange={setF("employment_type")}>
                <option value="">All types</option>
                {meta?.employment_types.map((t) => <option key={t}>{t}</option>)}
              </select>
              <select data-testid="filter-salary" className={selectCls} value={filters.salary_min} onChange={setF("salary_min")}>
                {SALARY_OPTIONS.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-ink/8">
              <p className="text-sm text-muted" data-testid="jobs-count">
                {loading ? "Searching..." : <><span className="font-bold text-ink">{total}</span> jobs · <span className="font-bold text-gold">{totalOpenings}</span> open positions</>}
              </p>
              <button onClick={reset} data-testid="reset-filters" className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted hover:text-gold transition-colors"><RotateCcw size={13} /> Reset</button>
            </div>
          </div>

          {loading ? (
            <div className="grid gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-40 bg-white border border-ink/8 rounded-sm animate-pulse" />)}</div>
          ) : jobs.length === 0 ? (
            <div className="bg-white border border-ink/10 rounded-sm p-12 text-center" data-testid="no-jobs-message">
              <Briefcase size={36} className="text-gold mx-auto mb-4" />
              <h3 className="font-display text-2xl text-ink mb-3">No current openings match your filters</h3>
              <p className="text-sm text-muted max-w-lg mx-auto">No current openings are available for this combination. Submit your profile and our recruitment team may contact you when a suitable opportunity becomes available.</p>
              <a href="#profile" className="inline-block mt-6"><OutlineButton>Submit Your Profile</OutlineButton></a>
            </div>
          ) : (
            <div className="grid gap-4" data-testid="jobs-list">
              {jobs.map((job) => (
                <div key={job.id} data-testid={`job-card-${job.id.slice(0, 8)}`} className={`group bg-white border rounded-sm p-5 sm:p-6 transition-all duration-300 hover:shadow-[0_14px_36px_rgba(6,27,58,0.1)] ${selected[job.id] ? "border-gold" : "border-ink/10 hover:border-gold/40"}`}>
                  <div className="flex items-start gap-4">
                    <input
                      type="checkbox"
                      data-testid={`job-select-${job.id.slice(0, 8)}`}
                      checked={!!selected[job.id]}
                      onChange={() => toggleSelect(job.id)}
                      className="mt-1.5 w-5 h-5 accent-[#C99A3D] cursor-pointer"
                      aria-label={`Select ${job.title}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                        <div>
                          <button onClick={() => setDetailJob(job)} data-testid={`job-title-${job.id.slice(0, 8)}`} className="text-left font-display text-xl text-ink hover:text-navy transition-colors duration-200 leading-snug">{job.title}</button>
                          <p className="text-sm text-muted mt-1">{job.company} · <span className="inline-flex items-center gap-1"><MapPin size={12} />{job.location}</span></p>
                        </div>
                        <p className="font-mono text-sm font-bold text-navy whitespace-nowrap">{job.salary_display}</p>
                      </div>
                      <div className="mt-3.5 flex flex-wrap gap-2 text-[11px]">
                        <span className="inline-flex items-center gap-1 font-semibold text-ink/70 bg-steel px-2.5 py-1 rounded-sm"><Briefcase size={11} className="text-gold" />{job.qualification}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-ink/70 bg-steel px-2.5 py-1 rounded-sm">{job.experience}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-ink/70 bg-steel px-2.5 py-1 rounded-sm"><Clock size={11} className="text-gold" />{job.shift}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-ink/70 bg-steel px-2.5 py-1 rounded-sm">{job.employment_type}</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-ink/70 bg-steel px-2.5 py-1 rounded-sm"><Users size={11} className="text-gold" />{job.openings} openings</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-navy bg-gold/10 border border-gold/25 px-2.5 py-1 rounded-sm">{job.industry}</span>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2.5">
                        <button onClick={() => setApplyJobs([job])} data-testid={`job-apply-${job.id.slice(0, 8)}`} className="btn-sweep bg-navy text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm hover:bg-darknavy transition-colors duration-300">Apply Now</button>
                        <button onClick={() => setDetailJob(job)} data-testid={`job-details-${job.id.slice(0, 8)}`} className="border border-ink/15 text-ink text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm hover:border-gold hover:text-gold transition-colors duration-300">Details</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-10" data-testid="jobs-pagination">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="page-prev" className="inline-flex items-center gap-1 border border-ink/15 rounded-sm px-4 py-2.5 text-sm font-semibold text-ink disabled:opacity-40 hover:border-gold transition-colors"><ChevronLeft size={15} /> Prev</button>
              <span className="text-sm text-muted font-semibold">Page {page} of {pages}</span>
              <button disabled={page === pages} onClick={() => setPage(page + 1)} data-testid="page-next" className="inline-flex items-center gap-1 border border-ink/15 rounded-sm px-4 py-2.5 text-sm font-semibold text-ink disabled:opacity-40 hover:border-gold transition-colors">Next <ChevronRight size={15} /></button>
            </div>
          )}
        </div>
      </section>

      <section id="profile" className="bg-white py-16 sm:py-24 scroll-mt-20" data-testid="profile-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Can't Find the Right Opening?" title="Submit your profile" description="Share your details and our recruitment team may contact you when a suitable opportunity becomes available." />
          <Reveal delay={0.15}>
            <div className="mt-12 border border-ink/10 rounded-sm p-6 sm:p-10 bg-steel/50">
              <ProfileForm />
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {selectedJobs.length > 0 && !applyJobs && (
          <motion.div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] bg-darknavy border border-gold/40 rounded-sm shadow-[0_16px_48px_rgba(6,27,58,0.5)] px-4 sm:px-6 py-3.5 flex flex-wrap items-center justify-center gap-3 sm:gap-5 max-w-[94vw]"
            initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
            data-testid="bulk-apply-bar"
          >
            <p className="text-sm text-white"><span className="font-mono font-bold text-lightgold">{selectedJobs.length}</span> job{selectedJobs.length > 1 ? "s" : ""} selected</p>
            <button onClick={() => setApplyJobs(selectedJobs)} data-testid="bulk-apply-button" className="bg-gold text-darknavy text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm hover:bg-lightgold transition-colors">Apply to Selected</button>
            <button onClick={() => setSelected({})} data-testid="bulk-clear" className="text-white/50 hover:text-white transition-colors" aria-label="Clear selection"><X size={17} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detailJob && <JobDetail job={detailJob} onClose={() => setDetailJob(null)} onApply={(j) => { setDetailJob(null); setApplyJobs([j]); }} />}
      </AnimatePresence>
      {applyJobs && <ApplyModal jobs={applyJobs} onClose={() => { setApplyJobs(null); setSelected({}); }} />}
    </main>
  );
}
